import { computed, reactive, ref } from 'vue'
import { useApolloClient } from '@vue/apollo-composable'
import { useAuthStore } from '@/stores/authStore'
import { createPersistedJson } from '~/utils/persistedJson'
import {
  IMPERSONATED_CASE_INBOXES_QUERY,
  IMPERSONATED_CONTACT_INBOXES_QUERY,
  IMPERSONATED_MISSED_INBOXES_QUERY,
  IMPERSONATED_STAFF_INBOXES_QUERY,
  MARK_CASE_REVIEWED_MUTATION,
  MARK_CONTACT_REVIEWED_MUTATION,
  MARK_MISSED_REVIEWED_MUTATION,
  MARK_STAFF_REVIEWED_MUTATION,
} from '~/graphql/inbox/impersonation'
import {
  buildImpersonationGroups,
  impersonationGrandTotal,
  normalizeSavedTargets,
  toContactImpersonationCard,
  toImpersonationCard,
  toMissedImpersonationCard,
  toStaffImpersonationCard,
} from '~/utils/inboxImpersonation'

/**
 * Inbox impersonation — the module-level singleton behind the "mirror teammates'
 * inboxes" bar + sections.
 * ===========================================================================
 * SURFACE-AGNOSTIC (Sandy 2026-09-17): the teammate SELECTION is ONE shared
 * thing (the common bar) — tick @Betty once and you see her mirrored on Cases
 * AND Staff. Each inbox surface (`case`, `staff`, …) keeps its OWN mirror data
 * (groups + total + poll), fetched for the shared selection. `useInboxImpersonation(surface)`
 * hands a page its surface's data plus the shared selection controls.
 *
 * The selection is persisted per user in localStorage (`inboxImpersonation:`),
 * MULTI-SELECT, and a legacy single-target object is migrated on read. The
 * mirror is READ-ONLY of the teammates: "Reviewed" clears a card from MY mirror
 * (`markReviewed`), never their read state.
 */

const STORAGE_PREFIX = 'inboxImpersonation:'
const POLL_MS = 15_000

// How to fetch, unpack and shape one surface's mirror. Adding contacts later is
// one more entry here + its server resolver.
const SURFACES = {
  case: {
    query: IMPERSONATED_CASE_INBOXES_QUERY,
    dataKey: 'getImpersonatedCaseInboxes',
    itemsKey: 'cases',
    reviewMutation: MARK_CASE_REVIEWED_MUTATION,
    reviewIdVar: 'caseId',
    toCard: toImpersonationCard,
  },
  staff: {
    query: IMPERSONATED_STAFF_INBOXES_QUERY,
    dataKey: 'getImpersonatedStaffInboxes',
    itemsKey: 'staff',
    reviewMutation: MARK_STAFF_REVIEWED_MUTATION,
    reviewIdVar: 'staffId',
    toCard: toStaffImpersonationCard,
  },
  contact: {
    query: IMPERSONATED_CONTACT_INBOXES_QUERY,
    dataKey: 'getImpersonatedContactInboxes',
    itemsKey: 'contacts',
    reviewMutation: MARK_CONTACT_REVIEWED_MUTATION,
    reviewIdVar: 'contactId',
    toCard: toContactImpersonationCard,
  },

  // Missed calls: the card's `entityId` is the matched case/staff/contact, so the
  // Reviewed pointer keys off `contactId` (the stable per-caller id) instead.
  call: {
    query: IMPERSONATED_MISSED_INBOXES_QUERY,
    dataKey: 'getImpersonatedMissedInboxes',
    itemsKey: 'calls',
    reviewMutation: MARK_MISSED_REVIEWED_MUTATION,
    reviewIdVar: 'contactId',
    idField: 'contactId',
    toCard: toMissedImpersonationCard,
  },
}

/** The SHARED teammate selection — one bar, applied to every inbox surface. */
const selection = reactive({ userId: null, targets: [] })

/** Per-surface mirror state (groups + total + poll bookkeeping). */
const surfaceState = {}
function stateFor(surface) {
  if (!surfaceState[surface]) {
    surfaceState[surface] = {
      groups: ref([]),
      total: ref(0),
      running: false,
      timer: null,
      inFlight: false,
    }
  }

  return surfaceState[surface]
}

let _client = null
let _visibilityBound = false

function storageKey(userId) {
  return `${STORAGE_PREFIX}${userId ?? 'anon'}`
}

function syncSelection(userId) {
  const saved = createPersistedJson(storageKey(userId), null).read()

  // Accepts the legacy single-target object AND the new array (multi-select).
  selection.targets = normalizeSavedTargets(saved)
  selection.userId = userId
}

function persistSelection() {
  createPersistedJson(storageKey(selection.userId), null).write(selection.targets)
}

async function _fetchSurface(surface) {
  if (!_client) return

  const st = stateFor(surface)
  const cfg = SURFACES[surface]
  const ids = selection.targets.map(t => t.id).filter(id => id != null)

  // Nobody mirrored → this surface's sections + badge are empty, no request.
  if (!ids.length) {
    st.groups.value = []
    st.total.value = 0

    return
  }

  if (st.inFlight) return
  st.inFlight = true
  try {
    const { data } = await _client().query({
      query: cfg.query,
      variables: { targetUserIds: ids.map(String) },
      fetchPolicy: 'network-only',
    })

    const serverGroups = data?.[cfg.dataKey] ?? []

    st.groups.value = buildImpersonationGroups(serverGroups, selection.targets, {
      itemsKey: cfg.itemsKey,
      toCard: cfg.toCard,
    })
    st.total.value = impersonationGrandTotal(st.groups.value)
  }
  catch { /* transient — next tick retries */ }
  finally { st.inFlight = false }
}

function _startTimer(surface) {
  const st = stateFor(surface)
  if (st.timer || !st.running || !_client || !selection.targets.length) return
  st.timer = setInterval(() => _fetchSurface(surface), POLL_MS)
}

function _stopTimer(surface) {
  const st = surfaceState[surface]
  if (st?.timer) {
    clearInterval(st.timer)
    st.timer = null
  }
}

function _runningSurfaces() {
  return Object.keys(surfaceState).filter(s => surfaceState[s].running)
}

function _onVisibility() {
  if (document.hidden) {
    _runningSurfaces().forEach(_stopTimer)

    return
  }
  for (const surface of _runningSurfaces()) {
    _fetchSurface(surface)
    _startTimer(surface)
  }
}

/** Reset on logout so the next user never inherits the previous mirror. */
export function resetInboxImpersonationState() {
  for (const surface of Object.keys(surfaceState)) {
    const st = surfaceState[surface]

    st.running = false
    _stopTimer(surface)
    st.groups.value = []
    st.total.value = 0
  }

  if (_visibilityBound && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', _onVisibility)
    _visibilityBound = false
  }

  _client = null
  selection.userId = null
  selection.targets = []
}

export function useInboxImpersonation(surface = 'case') {
  const { resolveClient } = useApolloClient()
  const authStore = useAuthStore()
  const userId = authStore.authenticatedUser?.id ?? 'anon'

  if (selection.userId !== userId) syncSelection(userId)

  const cfg = SURFACES[surface] ?? SURFACES.case
  const key = SURFACES[surface] ? surface : 'case'
  const st = stateFor(key)

  /** Start THIS surface's poll (no-op when already running / not an LA member). */
  function start() {
    if (!authStore.isLeadAgencyUser) return

    _client = resolveClient

    if (typeof document !== 'undefined' && !_visibilityBound) {
      document.addEventListener('visibilitychange', _onVisibility)
      _visibilityBound = true
    }

    if (st.running) return
    st.running = true

    _fetchSurface(key)
    _startTimer(key)
  }

  /** Toggle a teammate in the SHARED selection; refetch every running surface. */
  function toggleTarget(peer) {
    if (peer?.id == null) return

    const pid = String(peer.id)
    const exists = selection.targets.some(t => String(t.id) === pid)

    selection.targets = exists
      ? selection.targets.filter(t => String(t.id) !== pid)
      : [
        ...selection.targets,
        {
          id: pid,
          label: peer.label ?? peer.name ?? peer.shortName ?? null,
          color: peer.color ?? null,
        },
      ]
    persistSelection()

    for (const s of _runningSurfaces()) {
      _stopTimer(s)
      _fetchSurface(s)
      _startTimer(s)
    }
  }

  /** Stop mirroring everyone (shared) — clears every surface. */
  function clearTargets() {
    selection.targets = []
    persistSelection()

    for (const s of _runningSurfaces()) {
      _stopTimer(s)
      _fetchSurface(s)
    }
  }

  /** OPTIMISTIC review of one card in THIS surface — flags it (stays under All).
   * The match key is the surface's `idField` (missed calls key by `contactId`,
   * not `entityId`); the total drops by the card's message count, or by 1 when
   * the count IS the card (missed calls count callers, not messages). */
  function dismiss(reviewId, targetId) {
    const rid = String(reviewId)
    const tid = String(targetId)
    const idField = cfg.idField ?? 'entityId'

    st.groups.value = st.groups.value.map(g => {
      if (String(g.targetId) !== tid) return g
      const entry = g.cases.find(e => String(e[idField]) === rid)
      if (!entry || entry.reviewed) return g

      return {
        ...g,
        cases: g.cases.map(e =>
          String(e[idField]) === rid ? { ...e, reviewed: true } : e,
        ),
        total: Math.max(0, g.total - (entry.messages?.count ?? 1)),
      }
    })
    st.total.value = impersonationGrandTotal(st.groups.value)
  }

  /** "Reviewed" — clear the card from MY mirror of THAT teammate (this surface). */
  async function markReviewed(entityId, targetId) {
    if (targetId == null) return

    dismiss(entityId, targetId)
    try {
      await (_client ?? resolveClient)().mutate({
        mutation: cfg.reviewMutation,
        variables: {
          targetUserId: String(targetId),
          [cfg.reviewIdVar]: String(entityId),
        },
      })
      _fetchSurface(key)
    }
    catch (err) {
      console.error('[inbox-impersonation] markReviewed failed', err)
      _fetchSurface(key) // re-sync so the card comes back if the write didn't land
    }
  }

  function refresh() {
    _fetchSurface(key)
  }

  return {
    targets: computed(() => selection.targets),
    targetIds: computed(() => selection.targets.map(t => t.id)),
    groups: computed(() => st.groups.value),
    total: computed(() => st.total.value),
    start,
    toggleTarget,
    clearTargets,
    markReviewed,
    refresh,
    stop: resetInboxImpersonationState,
  }
}
